import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Monitor, Smartphone, Tablet } from 'lucide-react';

interface ScreenLockInstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type DeviceType = 'mac' | 'pc' | 'ios' | 'android';

export const ScreenLockInstructionsModal: React.FC<ScreenLockInstructionsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [selectedDevice, setSelectedDevice] = useState<DeviceType>('mac');

  // Detect device type on mount
  useEffect(() => {
    if (!isOpen) return;

    const userAgent = navigator.userAgent.toLowerCase();
    const platform = navigator.platform.toLowerCase();

    // Detect iOS
    if (/iphone|ipad|ipod/.test(userAgent) || (platform === 'macintel' && navigator.maxTouchPoints > 1)) {
      setSelectedDevice('ios');
    }
    // Detect Android
    else if (/android/.test(userAgent)) {
      setSelectedDevice('android');
    }
    // Detect Mac
    else if (/mac/.test(platform) || /macintosh/.test(userAgent)) {
      setSelectedDevice('mac');
    }
    // Default to PC (Windows)
    else {
      setSelectedDevice('pc');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50" 
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative z-10 bg-base-100 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-base-300 sticky top-0 bg-base-100 z-10">
          <h2 className="text-2xl font-semibold text-base-content">
            Screen Lock Configuration
          </h2>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-circle btn-sm"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <p className="text-base-content/70">
            To prevent connection issues during recording, configure your device to keep the screen active. 
            Select your device type below.
          </p>

          {/* Device Selection Radio Buttons */}
          <div className="flex flex-wrap gap-4 pb-4 border-b border-base-300">
            <label className="flex items-center gap-2 cursor-pointer py-1">
              <input
                type="radio"
                name="deviceType"
                value="mac"
                checked={selectedDevice === 'mac'}
                onChange={(e) => setSelectedDevice(e.target.value as DeviceType)}
                className="radio radio-primary"
              />
              <span className="text-base-content text-sm">Mac</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer py-1">
              <input
                type="radio"
                name="deviceType"
                value="pc"
                checked={selectedDevice === 'pc'}
                onChange={(e) => setSelectedDevice(e.target.value as DeviceType)}
                className="radio radio-primary"
              />
              <span className="text-base-content text-sm">PC</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer py-1">
              <input
                type="radio"
                name="deviceType"
                value="ios"
                checked={selectedDevice === 'ios'}
                onChange={(e) => setSelectedDevice(e.target.value as DeviceType)}
                className="radio radio-primary"
              />
              <span className="text-base-content text-sm">iOS Mobile</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer py-1">
              <input
                type="radio"
                name="deviceType"
                value="android"
                checked={selectedDevice === 'android'}
                onChange={(e) => setSelectedDevice(e.target.value as DeviceType)}
                className="radio radio-primary"
              />
              <span className="text-base-content text-sm">Android Mobile</span>
            </label>
          </div>

          {/* Mac Instructions */}
          {selectedDevice === 'mac' && (
            <div className="card bg-base-200 border border-base-300">
            <div className="card-body">
              <div className="flex items-center gap-3 mb-3">
                <Monitor className="w-6 h-6 text-primary" />
                <h3 className="text-lg font-semibold text-base-content">Mac</h3>
              </div>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-base-content font-semibold text-sm">
                    1
                  </div>
                  <div>
                    <p className="text-base-content/80 font-medium">Open System Preferences</p>
                    <p className="text-sm text-base-content/60 mt-1">
                      Click the Apple menu → System Preferences → Energy Saver (or Battery on newer Macs)
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-base-content font-semibold text-sm">
                    2
                  </div>
                  <div>
                    <p className="text-base-content/80 font-medium">Adjust Display Sleep</p>
                    <p className="text-sm text-base-content/60 mt-1">
                      Set "Turn display off after" to "Never" or a long duration (30+ minutes) while recording
                    </p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-base-300 rounded-lg">
                  <img 
                    src="/placeholder-mac-settings.png" 
                    alt="Mac screen lock settings"
                    className="w-full rounded border border-base-300"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              </div>
            </div>
            </div>
          )}

          {/* PC (Windows) Instructions */}
          {selectedDevice === 'pc' && (
            <div className="card bg-base-200 border border-base-300">
              <div className="card-body">
                <div className="flex items-center gap-3 mb-3">
                  <Monitor className="w-6 h-6 text-primary" />
                  <h3 className="text-lg font-semibold text-base-content">PC (Windows)</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-base-content font-semibold text-sm">
                      1
                    </div>
                    <div>
                      <p className="text-base-content/80 font-medium">Open Power & Sleep Settings</p>
                      <p className="text-sm text-base-content/60 mt-1">
                        Settings → System → Power & sleep
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-base-content font-semibold text-sm">
                      2
                    </div>
                    <div>
                      <p className="text-base-content/80 font-medium">Adjust Screen Timeout</p>
                      <p className="text-sm text-base-content/60 mt-1">
                        Set "Screen" to "Never" or a long duration (30+ minutes) while recording
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-base-300 rounded-lg">
                    <img 
                      src="/placeholder-pc-settings.png" 
                      alt="PC screen lock settings"
                      className="w-full rounded border border-base-300"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* iOS Instructions */}
          {selectedDevice === 'ios' && (
            <div className="card bg-base-200 border border-base-300">
            <div className="card-body">
              <div className="flex items-center gap-3 mb-3">
                <Smartphone className="w-6 h-6 text-primary" />
                <h3 className="text-lg font-semibold text-base-content">iOS / iPhone / iPad</h3>
              </div>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-base-content font-semibold text-sm">
                    1
                  </div>
                  <div>
                    <p className="text-base-content/80 font-medium">Open Control Center</p>
                    <p className="text-sm text-base-content/60 mt-1">
                      Swipe down from the top-right corner (iPhone X and later) or swipe up from the bottom (older models)
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-base-content font-semibold text-sm">
                    2
                  </div>
                  <div>
                    <p className="text-base-content/80 font-medium">Enable Screen Lock</p>
                    <p className="text-sm text-base-content/60 mt-1">
                      Tap the lock icon with a circle around it to prevent the screen from dimming
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-base-content font-semibold text-sm">
                    3
                  </div>
                  <div>
                    <p className="text-base-content/80 font-medium">Alternative: Settings Method</p>
                    <p className="text-sm text-base-content/60 mt-1">
                      Settings → Display & Brightness → Auto-Lock → Set to "Never" (temporarily)
                    </p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-base-300 rounded-lg">
                  <img 
                    src="/placeholder-ios-settings.png" 
                    alt="iOS screen lock settings"
                    className="w-full rounded border border-base-300"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              </div>
            </div>
            </div>
          )}

          {/* Android Instructions */}
          {selectedDevice === 'android' && (
            <div className="card bg-base-200 border border-base-300">
            <div className="card-body">
              <div className="flex items-center gap-3 mb-3">
                <Tablet className="w-6 h-6 text-primary" />
                <h3 className="text-lg font-semibold text-base-content">Android</h3>
              </div>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-base-content font-semibold text-sm">
                    1
                  </div>
                  <div>
                    <p className="text-base-content/80 font-medium">Open Quick Settings</p>
                    <p className="text-sm text-base-content/60 mt-1">
                      Swipe down twice from the top of the screen to access full Quick Settings
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-base-content font-semibold text-sm">
                    2
                  </div>
                  <div>
                    <p className="text-base-content/80 font-medium">Enable Stay Awake</p>
                    <p className="text-sm text-base-content/60 mt-1">
                      Look for "Stay awake" or "Screen timeout" and set it to a long duration or "Keep screen on"
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-base-content font-semibold text-sm">
                    3
                  </div>
                  <div>
                    <p className="text-base-content/80 font-medium">Alternative: Settings Method</p>
                    <p className="text-sm text-base-content/60 mt-1">
                      Settings → Display → Screen timeout → Set to maximum (30 minutes) or use developer options
                    </p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-base-300 rounded-lg">
                  <img 
                    src="/placeholder-android-settings.png" 
                    alt="Android screen lock settings"
                    className="w-full rounded border border-base-300"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              </div>
            </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-base-300">
          <button
            className="btn btn-primary"
            onClick={onClose}
          >
            Got it
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

